import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { makeApiRequest, API_ENDPOINTS } from '../config/api';
import './Products.css';

interface Capability {
  title: string;
  description: string;
}

interface ProductService {
  id: string;
  name: string;
  description?: string;
  heading?: string;
  subheading?: string;
  video?: string;
  image: string;
  slug: string;
  order?: number;
  capabilities?: Capability[];
}

interface ServicesContent {
  hero?: {
    title: string;
    subtitle: string;
  };
  products_services: ProductService[];
}

interface PageData {
  id: string;
  title: string;
  content: ServicesContent;
  meta_description: string;
  updated_at: string;
  services: ProductService[];
}

const Products: React.FC = () => {
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('Products useEffect running - should only run once');
    const fetchData = async () => {
      try {
        console.log('Fetching products data');
        const response = await makeApiRequest(API_ENDPOINTS.PRODUCTS_SERVICES);

        if (response.ok) {
          const data = await response.json();
          setPageData(data);
        } else {
          setError('Failed to load products data');
        }
      } catch (err) {
        console.error('Error fetching services data:', err);
        setError('Failed to load products data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="products-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Products...</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="products-page">
        <div className="error-container">
          <h2>Error Loading Page</h2>
          <p>{error || 'Failed to load products data'}</p>
        </div>
      </div>
    );
  }

  const { content } = pageData || {};
  const productsServices = pageData.services || content?.products_services || [];
  const primeProduct = productsServices.find(p => p.id === 'prime') || productsServices[0];
  const primeDetails = productsServices.find(p => p.id === 'enterprise-solutions');
  const auraProduct = productsServices.find(p => p.id === 'aura');

  if (!primeProduct) {
    return (
      <div className="products-page">
        <div className="error-container">
          <h2>No Products Available</h2>
          <p>Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      {/* Hero Section */}
      <section className="product-hero">
        <div className="product-container">
          <h1 className="page-title">{content?.hero?.title || 'Our Product'}</h1>

          <div className="product-showcase">
            <div className="product-image-section">
              {primeProduct.video ? (
                <video
                  className="product-main-video"
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster={primeProduct.image}
                >
                  <source src={primeProduct.video} type="video/mp4" />
                  <img
                    src={primeProduct.image}
                    alt={primeProduct.name}
                    className="product-main-image"
                  />
                </video>
              ) : (
                <img
                  src={primeProduct.image}
                  alt={primeProduct.name}
                  className="product-main-image"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/uploads/products-services/placeholder.jpg';
                  }}
                />
              )}
            </div>

            <div className="product-info-section">
              <h2 className="product-subtitle">{content?.hero?.subtitle || 'The Cognitive Digital Brain'}</h2>
              <h3 className="product-name">{primeProduct.name}</h3>
              <p className="product-description">{primeProduct.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Prime Details Section - Full Width */}
      {primeDetails && (
        <section className="product-details">
          <div className="product-container">
            <div className="product-details-full">
              <p className="details-heading">{primeDetails.heading}</p>
              <p className="details-subheading">{primeDetails.subheading}</p>

              {primeDetails.capabilities && (
                <ul className="capabilities-list">
                  {primeDetails.capabilities.map((capability, index) => (
                    <li key={index} className="capability-item">
                      <span className="capability-title">{capability.title}:</span>{' '}
                      <span className="capability-description">{capability.description}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Aura Section */}
      {auraProduct && (
        <section className="product-aura">
          <div className="product-container">
            <div className="product-showcase">
              <div className="product-image-section">
                {auraProduct.video ? (
                  <video
                    className="product-main-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster={auraProduct.image}
                  >
                    <source src={auraProduct.video} type="video/mp4" />
                    <img
                      src={auraProduct.image}
                      alt={auraProduct.name}
                      className="product-main-image"
                    />
                  </video>
                ) : (
                  <img
                    src={auraProduct.image}
                    alt={auraProduct.name}
                    className="product-main-image"
                  />
                )}
              </div>

              <div className="product-info-section">
                <h2 className="aura-title">{auraProduct.name}</h2>
                <h3 className="aura-subtitle">{auraProduct.heading}</h3>
                <p className="aura-intro">{auraProduct.subheading}</p>
                <p className="product-description">{auraProduct.description}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="products-cta">
        <div className="product-container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Industry?</h2>
            <p className="cta-description">
              Discover how our AI solutions are tailored to meet the unique challenges and opportunities of your specific industry.
            </p>
            <Link to="/industries" className="cta-button">
              Explore Industries We Serve
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
